import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { recipesService, RecipeCreateEntry } from '../services/recipesService';
import { ingredientsService } from '../services/ingredientsService';
import { productsService } from '../services/productsService';
import {
  Recipe,
  RecipeIngredient,
  Ingredient,
  ProductCategory,
} from 'public-contracts';
import { ModernIngredientForm } from '../ingredients/ModernIngredientForm';
import { useFileUpload } from '../hooks/useFileUpload';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { IngredientCombobox } from '../components/IngredientCombobox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

// Lucide React icons
import {
  ChefHat,
  Save,
  X,
  AlertTriangle,
  Plus,
  Trash2,
  Edit,
  Beaker,
  Upload,
} from 'lucide-react';

interface RecipeFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (recipe: Recipe) => void;
  recipe?: Recipe | null;
}

export const ModernRecipeForm: React.FC<RecipeFormProps> = ({
  open,
  onClose,
  onSave,
  recipe,
}) => {
  const { translate } = useTranslation();
  const { uploadFile, getFileUrl } = useFileUpload();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [editingIngredientIndex, setEditingIngredientIndex] = useState<
    number | null
  >(null);
  const [ingredientFormOpen, setIngredientFormOpen] = useState(false);
  const [nestingWarning, setNestingWarning] = useState<{
    maxLevel: number;
    valid: boolean;
    errors: string[];
  } | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState<RecipeCreateEntry>({
    name: '',
    description: '',
    imageId: '',
    productCategoryId: '',
    ingredients: [],
    products: [],
  });

  const [newIngredient, setNewIngredient] = useState<{
    ingredientId: string;
    amount: number;
    unit: string;
  }>({
    ingredientId: '',
    amount: 0,
    unit: 'g',
  });

  useEffect(() => {
    if (open) {
      loadIngredients();
      loadCategories();
    }
  }, [open]);

  // Separate effect to update form data when recipe changes
  // This runs after categories might be loaded (but doesn't wait for them)
  useEffect(() => {
    if (recipe) {
      console.log('Setting form data from recipe:', recipe);
      setFormData({
        name: recipe.name || '',
        description: recipe.description || '',
        imageId: recipe.imageId || '',
        productCategoryId: recipe.productCategoryId || '',
        ingredients: recipe.ingredients || [],
        products: recipe.products || [],
      });
    } else if (open && !recipe) {
      // Reset form when opening without a recipe (create mode)
      setFormData({
        name: '',
        description: '',
        imageId: '',
        productCategoryId: '',
        ingredients: [],
        products: [],
      });
    }
  }, [recipe, open]);

  // Ensure form data is updated when categories load (fixes race condition)
  // This ensures the Select component has the category option available
  useEffect(() => {
    if (recipe && categories.length > 0) {
      // Re-set productCategoryId to ensure Select component recognizes it
      // This is needed because Select needs the option to exist in SelectContent
      setFormData((prev) => {
        const categoryId = recipe.productCategoryId || '';
        // Only update if the category exists in the loaded categories
        const categoryExists = !categoryId || categories.some(cat => cat.id === categoryId);
        if (categoryExists && prev.productCategoryId !== categoryId) {
          return {
            ...prev,
            productCategoryId: categoryId,
          };
        }
        return prev;
      });
    }
  }, [categories, recipe]);

  // Load image preview when imageId changes
  useEffect(() => {
    const loadImagePreview = async () => {
      if (formData.imageId) {
        try {
          const url = await getFileUrl(formData.imageId);
          setImagePreview(url || '');
        } catch (error) {
          console.error('Error loading image preview:', error);
          setImagePreview('');
        }
      } else {
        setImagePreview('');
      }
    };
    loadImagePreview();
  }, [formData.imageId, getFileUrl]);

  // Reset new ingredient form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setNewIngredient({
        ingredientId: '',
        amount: 0,
        unit: 'g',
      });
      setEditingIngredientIndex(null);
    }
  }, [open]);

  const loadIngredients = async () => {
    try {
      const data = await ingredientsService.getIngredients({
        page: 1,
        size: 1000, // Fetch all ingredients
      });
      setIngredients(data);
    } catch (err) {
      console.error('Failed to load ingredients:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await productsService.getProductCategories({
        page: 1,
        perPage: 1000, // Fetch all categories for dropdown
      });
      const categories = Array.isArray(categoriesData) ? categoriesData : [];
      setCategories(categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setCategories([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setNestingWarning(null);

    try {
      // Clean data
      const dataToSave = {
        ...formData,
        productCategoryId: formData.productCategoryId || undefined,
      };

      let savedRecipe: Recipe;

      if (recipe) {
        savedRecipe = await recipesService.updateRecipe(recipe.id, dataToSave);
      } else {
        savedRecipe = await recipesService.createRecipe(dataToSave);
      }

      // Check nesting levels after saving
      try {
        const nestingCheck = await recipesService.checkRecipeNestingLevel(savedRecipe.id);
        if (!nestingCheck.valid) {
          setNestingWarning({
            maxLevel: nestingCheck.maxLevel,
            valid: nestingCheck.valid,
            errors: nestingCheck.errors,
          });
          setError(
            `Recipe saved but nesting level validation failed: ${nestingCheck.errors.join(', ')}. Please simplify the recipe structure.`
          );
          return;
        }
      } catch (nestingErr) {
        // Log but don't block save if nesting check fails
        console.error('Failed to check nesting level:', nestingErr);
      }

      onSave(savedRecipe);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save recipe');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof RecipeCreateEntry, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(translate('pos.recipes.errors.invalidImageType', 'Please upload an image file'));
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);
      const fileId = await uploadFile(file);
      if (fileId) {
        handleInputChange('imageId', fileId);
        // Load preview for the newly uploaded image
        const url = await getFileUrl(fileId);
        setImagePreview(url || '');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.recipes.errors.imageUploadError', 'Failed to upload image')
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddIngredient = () => {
    if (!newIngredient.ingredientId || newIngredient.amount <= 0) {
      return;
    }

    const ingredient: RecipeIngredient = {
      id: '',
      ingredientId: newIngredient.ingredientId,
      amount: newIngredient.amount,
      unit: newIngredient.unit,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setFormData((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, ingredient],
    }));

    setNewIngredient({
      ingredientId: '',
      amount: 0,
      unit: 'g',
    });
  };

  const handleEditIngredient = (index: number) => {
    const ingredient = formData.ingredients[index];
    setNewIngredient({
      ingredientId: ingredient.ingredientId,
      amount: ingredient.amount,
      unit: ingredient.unit,
    });
    setEditingIngredientIndex(index);
  };

  const handleUpdateIngredient = () => {
    if (
      editingIngredientIndex === null ||
      !newIngredient.ingredientId ||
      newIngredient.amount <= 0
    ) {
      return;
    }

    const updatedIngredients = [...formData.ingredients];
    updatedIngredients[editingIngredientIndex] = {
      ...updatedIngredients[editingIngredientIndex],
      ingredientId: newIngredient.ingredientId,
      amount: newIngredient.amount,
      unit: newIngredient.unit,
    };

    setFormData((prev) => ({
      ...prev,
      ingredients: updatedIngredients,
    }));

    setNewIngredient({
      ingredientId: '',
      amount: 0,
      unit: 'g',
    });
    setEditingIngredientIndex(null);
  };

  const handleRemoveIngredient = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  };

  const getIngredientName = (ingredientId: string) => {
    const ingredient = ingredients.find((i) => i.id === ingredientId);
    return ingredient?.name || ingredientId;
  };

  const isFormValid = () => {
    return (
      formData.name &&
      formData.description &&
      // productCategoryId is optional if no categories are available
      formData.ingredients.length > 0
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <ChefHat className="h-5 w-5" />
            <span>
              {recipe
                ? translate('pos.recipes.edit', 'Edit Recipe')
                : translate('pos.recipes.create', 'Create Recipe')}
            </span>
          </DialogTitle>
          <DialogDescription>
            {recipe
              ? 'Update recipe details and ingredients'
              : 'Create a new recipe with ingredients'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Nesting Level Warning */}
          {nestingWarning && !nestingWarning.valid && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">
                    Recipe nesting level exceeds maximum (2 levels)
                  </p>
                  <p className="text-sm">
                    Current maximum nesting level: {nestingWarning.maxLevel}
                  </p>
                  <ul className="text-sm list-disc list-inside">
                    {nestingWarning.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                  <p className="text-sm mt-2">
                    Please simplify the recipe structure. Ingredients can only have recipes up to 2 levels deep.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {translate('pos.recipes.basicInformation', 'Basic Information')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {translate('pos.recipes.fields.name', 'Name')} *
                  </Label>
                  <Input
                    id="name"
                    placeholder={translate(
                      'pos.recipes.placeholders.enterName',
                      'Enter recipe name'
                    )}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="productCategoryId">
                    {translate(
                      'pos.recipes.fields.productCategoryId',
                      'Product Category'
                    )}
                  </Label>
                  <Select
                    key={`category-select-${categories.length > 0 ? 'loaded' : 'loading'}-${recipe?.id || 'new'}`}
                    value={formData.productCategoryId || 'none'}
                    onValueChange={(value) =>
                      handleInputChange(
                        'productCategoryId',
                        value === 'none' ? '' : value
                      )
                    }
                  >
                    <SelectTrigger id="productCategoryId">
                      <SelectValue
                        placeholder={translate(
                          'pos.recipes.placeholders.selectCategory',
                          'Select category'
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        {translate('common.none', 'None')}
                      </SelectItem>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">
                  {translate('pos.recipes.fields.description', 'Description')} *
                </Label>
                <Textarea
                  id="description"
                  placeholder={translate(
                    'pos.recipes.placeholders.enterDescription',
                    'Enter recipe description'
                  )}
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="imageId">
                  {translate('pos.recipes.fields.imageId', 'Image')}
                </Label>
                {imagePreview && (
                  <div className="relative w-full">
                    <img
                      src={imagePreview}
                      alt={translate('pos.recipes.fields.imageId', 'Recipe image')}
                      className="w-full h-48 object-cover rounded-md border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        handleInputChange('imageId', '');
                        setImagePreview('');
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={uploadingImage}
                  asChild
                >
                  <label>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingImage
                      ? translate('common.uploading', 'Uploading...')
                      : imagePreview
                      ? translate('pos.recipes.changeImage', 'Change Image')
                      : translate('pos.recipes.uploadImage', 'Upload Image')}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Ingredients */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {translate('pos.recipes.fields.ingredients', 'Ingredients')} *
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add/Edit Ingredient Form */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
                <div className="space-y-2">
                  <Label>
                    {translate('pos.recipes.fields.ingredients', 'Ingredient')}
                  </Label>
                  <div className="flex gap-2">
                    <IngredientCombobox
                      value={newIngredient.ingredientId}
                      onValueChange={(value) =>
                        setNewIngredient((prev) => ({
                          ...prev,
                          ingredientId: value,
                        }))
                      }
                      placeholder={translate(
                        'pos.recipes.placeholders.selectIngredient',
                        'Select ingredient'
                      )}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setIngredientFormOpen(true)}
                      title={translate(
                        'pos.recipes.ingredient.quickCreate',
                        'Quick create ingredient'
                      )}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>
                    {translate('pos.recipes.fields.amount', 'Amount')}
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={translate(
                      'pos.recipes.placeholders.enterAmount',
                      '0'
                    )}
                    value={newIngredient.amount || ''}
                    onChange={(e) =>
                      setNewIngredient((prev) => ({
                        ...prev,
                        amount: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select
                    value={newIngredient.unit}
                    onValueChange={(value) =>
                      setNewIngredient((prev) => ({ ...prev, unit: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="g">g (grams)</SelectItem>
                      <SelectItem value="kg">kg (kilograms)</SelectItem>
                      <SelectItem value="ml">ml (milliliters)</SelectItem>
                      <SelectItem value="l">l (liters)</SelectItem>
                      <SelectItem value="pieces">pieces</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex items-end">
                  {editingIngredientIndex === null ? (
                    <Button
                      type="button"
                      onClick={handleAddIngredient}
                      className="w-full"
                      disabled={
                        !newIngredient.ingredientId || newIngredient.amount <= 0
                      }
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  ) : (
                    <div className="flex space-x-2 w-full">
                      <Button
                        type="button"
                        onClick={handleUpdateIngredient}
                        className="flex-1"
                        disabled={
                          !newIngredient.ingredientId ||
                          newIngredient.amount <= 0
                        }
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Update
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingIngredientIndex(null);
                          setNewIngredient({
                            ingredientId: '',
                            amount: 0,
                            unit: 'g',
                          });
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Ingredients List */}
              {formData.ingredients.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ingredient</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.ingredients.map((ingredient, index) => {
                      const ingredientData = ingredients.find(
                        (i) => i.id === ingredient.ingredientId
                      );
                      const hasRecipe = ingredientData?.recipeId;
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <span>{getIngredientName(ingredient.ingredientId)}</span>
                              {hasRecipe && (
                                <ChefHat
                                  className="h-4 w-4 text-muted-foreground"
                                  title="This ingredient has a recipe (nested)"
                                />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{ingredient.amount}</TableCell>
                          <TableCell>{ingredient.unit}</TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditIngredient(index)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveIngredient(index)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={!isFormValid() || loading}>
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {translate('common.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {translate('common.save', 'Save')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Quick Create Ingredient Dialog */}
      <ModernIngredientForm
        open={ingredientFormOpen}
        onClose={() => setIngredientFormOpen(false)}
        onSave={async (ingredient: Ingredient) => {
          // Reload ingredients to include the new one
          await loadIngredients();
          // Automatically select the newly created ingredient
          setNewIngredient((prev) => ({
            ...prev,
            ingredientId: ingredient.id,
          }));
          setIngredientFormOpen(false);
        }}
        ingredient={null}
      />
    </Dialog>
  );
};
